import React, { memo } from 'react';
import styled, { keyframes } from 'styled-components';

// Animações
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Estilos
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
  will-change: opacity;
`;

const ModalContent = styled.div`
  background: rgba(7, 15, 29, 0.95);
  border-radius: 10px;
  padding: 25px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 0 30px rgba(0, 150, 255, 0.3);
  border: 1px solid rgba(0, 150, 255, 0.4);
  position: relative;
  animation: ${fadeIn} 0.2s ease-out;
  will-change: opacity, transform;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, rgba(0, 100, 255, 0), rgba(0, 200, 255, 0.8), rgba(0, 100, 255, 0));
  }
`;

const ModalTitle = styled.h3`
  color: #fff;
  font-size: 1.3rem;
  margin-bottom: 20px;
  text-align: center;
  letter-spacing: 0.5px;
  text-shadow: 0 0 10px rgba(0, 150, 255, 0.5);
`;

const ModalDescription = styled.p`
  color: #8a94a7;
  font-size: 0.9rem;
  margin-bottom: 25px;
  text-align: center;
  line-height: 1.4;
  
  strong {
    color: #90CAFF;
    font-weight: 500;
  }
`;

const DayCheckboxContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 25px;
`;

const DayCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 0.95rem;
  cursor: pointer;
  padding: 10px 15px;
  border-radius: 5px;
  transition: all 0.2s ease;
  background: rgba(0, 30, 60, 0.3);
  border: 1px solid rgba(0, 150, 255, 0.2);
  
  &:hover {
    color: #90CAFF;
    background: rgba(0, 40, 80, 0.4);
    border-color: rgba(0, 150, 255, 0.4);
  }
  
  &.selected {
    background: rgba(0, 60, 100, 0.4);
    border-color: rgba(0, 200, 255, 0.5);
    box-shadow: 0 0 10px rgba(0, 150, 255, 0.2);
  }
`;

const CustomCheckbox = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  margin-right: 12px;
  position: relative;
  background: rgba(0, 20, 40, 0.6);
  border: 1px solid rgba(0, 150, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 2px;
    background: transparent;
    transition: all 0.2s ease;
  }
  
  &.checked::after {
    background: #90CAFF;
    box-shadow: 0 0 8px rgba(144, 202, 255, 0.5);
  }
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const TimeHighlight = styled.span`
  background: rgba(0, 150, 255, 0.15);
  color: #90CAFF;
  padding: 3px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9rem;
  margin: 0 3px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
`;

const ModalButton = styled.button`
  background: ${props => props.primary ? 'rgba(0, 150, 255, 0.2)' : 'rgba(50, 50, 50, 0.2)'};
  color: ${props => props.primary ? '#90CAFF' : '#8a94a7'};
  border: 1px solid ${props => props.primary ? 'rgba(0, 150, 255, 0.5)' : 'rgba(100, 100, 100, 0.3)'};
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.primary ? 'rgba(0, 150, 255, 0.3)' : 'rgba(50, 50, 50, 0.3)'};
    border-color: ${props => props.primary ? 'rgba(0, 200, 255, 0.7)' : 'rgba(100, 100, 100, 0.5)'};
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Componente memo para melhorar a performance do checkbox
const DayCheckbox = memo(({ day, isSelected, onToggle }) => {
  return (
    <DayCheckboxLabel 
      className={isSelected ? 'selected' : ''}
    >
      <HiddenCheckbox 
        type="checkbox" 
        checked={isSelected || false}
        onChange={() => onToggle(day)}
      />
      <CustomCheckbox className={isSelected ? 'checked' : ''} />
      {day.charAt(0).toUpperCase() + day.slice(1)}
    </DayCheckboxLabel>
  );
});

/**
 * Componente para modal de cópia de horários entre dias da semana
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.isOpen - Se o modal está aberto
 * @param {string} props.sourceDay - O dia de origem dos horários
 * @param {Object} props.selectedDays - Objeto com os dias selecionados
 * @param {Object} props.availability - Objeto com a disponibilidade
 * @param {function} props.onToggleDay - Função para alternar a seleção de um dia
 * @param {function} props.onApply - Função para aplicar os horários
 * @param {function} props.onClose - Função para fechar o modal
 * @param {React.RefObject} props.modalRef - Referência para o conteúdo do modal
 */
const CopyTimeModal = ({
  isOpen,
  sourceDay,
  selectedDays,
  availability,
  onToggleDay,
  onApply,
  onClose,
  modalRef
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent ref={modalRef}>
        <ModalTitle>Copiar Horários</ModalTitle>
        <ModalDescription>
          Copiar horários de <strong>{sourceDay.charAt(0).toUpperCase() + sourceDay.slice(1)}</strong> 
          (<TimeHighlight>{availability[sourceDay].startTime}</TimeHighlight> - 
          <TimeHighlight>{availability[sourceDay].endTime}</TimeHighlight>) 
          para os dias selecionados:
        </ModalDescription>
        
        <DayCheckboxContainer>
          {Object.keys(availability).map(day => (
            day !== sourceDay && (
              <DayCheckbox 
                key={day}
                day={day}
                isSelected={selectedDays[day]}
                onToggle={onToggleDay}
              />
            )
          ))}
        </DayCheckboxContainer>
        
        <ModalActions>
          <ModalButton onClick={onClose}>
            Cancelar
          </ModalButton>
          <ModalButton primary onClick={onApply}>
            Aplicar
          </ModalButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CopyTimeModal;
